import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import moment from 'moment';
import { debounceTime } from 'rxjs/operators';
import { FuncionarioService } from 'src/app/services/FuncionarioService/funcionario.service';
import { ServicoService } from 'src/app/services/ServicoService/servico.service';
import Swal from 'sweetalert2';

import { ITabelaFuncionario } from './listagem-motoristas.model';
import {
  formatarCpfCnpj,
  formatarTelefone
} from 'src/app/core/utils/utilities';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { MotoristaExternoService } from 'src/app/services/MotoristaExternoService/motorista-externo.service';
import {
  IGetMotoristasExternoParams,
  IGetMotoristasExternoResponseItem
} from 'src/app/services/MotoristaExternoService/motorista-externo.model';
import { ModalVincularCavaloCarretaComponent } from '../../empresa/modal-vincular-cavalo-carreta/modal-vincular-cavalo-carreta.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ICreateTermoUsoVersao, IGetLastTermoVersaoByTipoRequest, IGetLastTermoVersaoByTipoResult } from 'src/app/services/TermoUso/termoUso.model';
import { TermoUsoService } from 'src/app/services/TermoUso/termo-uso.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';

type IOptions = {
  name: string;
  value: string | number;
};

@Component({
  selector: 'app-listagem-motoristas',
  templateUrl: './listagem-motoristas.component.html',
  styleUrls: ['./listagem-motoristas.component.scss']
})
export class ListagemMotoristasComponent implements OnInit, AfterViewInit {
  isLoading: boolean = false;
  numeroPagina: number = 0;
  form = this._inicializarFormulario();
  tableData: ITabelaFuncionario[] | Array<any> = [];
  maxPages: number = 1;
  expandirCardsMobile: boolean = true;

  servicoOptions: IOptions[] = [
    {
      name: '',
      value: ''
    }
  ];

  tableColumns = [
    { header: 'Código', key: 'codigo', type: 'text' },
    { header: 'Nome', key: 'nome', type: 'text' },
    { header: 'CPF', key: 'cpf', type: 'text' },
    { header: 'CNH', key: 'cnh', type: 'text' },
    { header: 'Telefone', key: 'telefone', type: 'text' },
    { header: 'E-mail', key: 'email', type: 'text' },
    {
      header: 'Ações',
      key: 'actions',
      type: 'buttons',
      buttons: [
        {
          label: 'Visualizar',
          img: 'assets/eye-icon.png',
          action: (item: any) => this.visualizarMotorista(item)
        },
        {
          label: 'Vincular Cavalo ou Carreta',
          img: 'assets/bx-link.png',
          action: (item: any) => this.vincularCavaloOuCarreta(item)
        }
      ]
    }
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private motoristaExternoService: MotoristaExternoService,
    public swalAlert: SwalAlertService,
    public dialog: MatDialog,
    private termoUsoService: TermoUsoService,
    private appExportBase64: AppExportBase64
  ) {}

  private _inicializarFormulario() {
    const form = this.fb.group({
      NM_MTRT_EXTR: [''],
      TX_CPF_EXTR: [''],
      PAGE_NUMBER: [1],
      PAGE_SIZE: [10]
    });
    return form;
  }

  ngOnInit(): void {
    this.getMotoristas();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getMotoristas();
    });
  }

  navegarCadastroFuncionario() {
    this.router.navigate(['cadastro/funcionario']);
  }

  mudarPagina(pagina: number) {
    this.form.controls['PAGE_NUMBER'].setValue(pagina);
    this.getMotoristas();
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  getHours(date: Date) {
    if (date !== null) {
      return moment(date).format('HH:mm');
    }
    return '******';
  }

  getDate(date: Date) {
    if (date !== null) {
      return moment(date).format('DD/MM/YYYY');
    }
    return '******';
  }

  getMotoristas() {
    this.isLoading = true;
    const params = this.createParams();

    this.motoristaExternoService.GetMotoristasExterno(params).subscribe({
      next: response => {
        this.maxPages = response.TOTAL_PAGES;
        this.isLoading = false;
        this.tableData = response.ITEMS.map(
          (x: IGetMotoristasExternoResponseItem) => {
            let enderecoCompleto: string = '';
            if (
              x.TX_ENDE_RUA_EXTR &&
              x.TX_ENDE_CDDE_EXTR &&
              x.TX_ENDE_ESTD_EXTR
            ) {
              enderecoCompleto = `${x.TX_ENDE_RUA_EXTR}, ${x.TX_ENDE_CDDE_EXTR}, ${x.TX_ENDE_ESTD_EXTR}`;
            }
            return {
              codigo: x.PK_MTRT_EXTR ?? 0,
              nome: x.NM_MTRT_EXTR,
              cpf: formatarCpfCnpj(String(x.TX_CPF_EXTR), 'cpf'),
              cnh: x.TX_CNH_EXTR,
              telefone: formatarTelefone(String(x.NU_TELE_EXTR)),
              email: x.TX_MAIL_EXTR,
              endereco: enderecoCompleto,
              ...x
            };
          }
        );
      },
      error: (_error: any) => {
        this.isLoading = false;
      }
    });
  }

  createParams(): IGetMotoristasExternoParams {
    return {
      NM_MTRT_EXTR: this.form.controls['NM_MTRT_EXTR'].value ?? '',
      TX_CPF_EXTR: this.form.controls['TX_CPF_EXTR'].value ?? '',
      PAGE_NUMBER: this.form.controls['PAGE_NUMBER'].value ?? 1,
      PAGE_SIZE: this.form.controls['PAGE_SIZE'].value ?? 8
    };
  }

  visualizarMotorista(item: any) {
    const queryParams = {
      ...item
    };
    this.router.navigate(['visualizacao/motorista-externo'], { queryParams });
  }

  vincularCavaloOuCarreta(item: any) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.width = '680px';
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      FK_MTRT_EXTR: item.codigo
    };

    const dialogRef = this.dialog.open(
      ModalVincularCavaloCarretaComponent,
      dialogConfig
    );
  }

  uploadTermo(file: any, termo: number) {
    this.isLoading = true;

    const body: ICreateTermoUsoVersao = {
      file: file,
      tipoTermoUso: termo,
      isEpsa: false
    };

    this.termoUsoService.CreateTermoUsoVersao(body).subscribe({
      next: (response) => {
      },
      error: (err) => {
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  downloadArquivo(termoTipo: number) {
    this.isLoading = true;

    const body: IGetLastTermoVersaoByTipoRequest = {
      TipoTermo: termoTipo,
      IsEpsa: false
    };

    this.termoUsoService.GetLastTermoVersaoByTipo(body).subscribe({
      next: (response: IGetLastTermoVersaoByTipoResult) => {
        const nameFile = termoTipo === 1 ? 'TermoDeUso_Motorista_V' : 'TermoDeConsentimento_Motorista_V';
        this.appExportBase64.downloadPDF(
          response.File.Base64Content,
          nameFile + response.TermoId
        );

        this.isLoading = false;
      },
      error: _error => {
        this.isLoading = false;
      }
    });
  }
}
